import { Canvas } from 'fabric/fabric-impl';

type InsertPositionOptions = {
  step?: number;
  baseLeft?: number;
  baseTop?: number;
  cascadeCount?: number;
};

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

const isBackgroundLayer = (object: any) =>
  object?.isBackgroundLayer === true ||
  (object?.type === 'rect' && object?.selectable === false && object?.evented === false) ||
  (object?.type === 'rect' && object?.selectable === false && object?.lockMovementX === true && object?.lockMovementY === true);

const getNextInsertPosition = (canvas: Canvas | undefined, options?: InsertPositionOptions) => {
  const objects = canvas?.getObjects?.() || [];
  const background = (objects.find((object) => isBackgroundLayer(object)) || objects[0]) as any;
  const canvasWidth = typeof canvas?.getWidth === 'function' ? canvas.getWidth() : undefined;
  const canvasHeight = typeof canvas?.getHeight === 'function' ? canvas.getHeight() : undefined;
  const referenceWidth =
    typeof canvasWidth === 'number' && canvasWidth > 0
      ? canvasWidth
      : typeof background?.width === 'number'
        ? background.width
        : 1920;
  const referenceHeight =
    typeof canvasHeight === 'number' && canvasHeight > 0
      ? canvasHeight
      : typeof background?.height === 'number'
        ? background.height
        : 1080;
  const adaptiveStep = Math.round(clamp(Math.min(referenceWidth, referenceHeight) * 0.018, 18, 34));
  const step = options?.step ?? adaptiveStep;
  const adaptiveCascadeCount = Math.round(clamp(Math.min(referenceWidth, referenceHeight) / Math.max(step, 1), 18, 40));
  const cascadeCount = options?.cascadeCount ?? adaptiveCascadeCount;

  const defaultLeft =
    typeof options?.baseLeft === 'number'
      ? options.baseLeft
      : typeof referenceWidth === 'number'
        ? Math.max(24, referenceWidth * 0.16)
        : 120;
  const defaultTop =
    typeof options?.baseTop === 'number'
      ? options.baseTop
      : typeof referenceHeight === 'number'
        ? Math.max(24, referenceHeight * 0.2)
        : 160;

  if (!canvas) {
    return { left: defaultLeft, top: defaultTop };
  }

  const drawableObjects = canvas.getObjects().filter((object) => !isBackgroundLayer(object));

  // Use the current drawable-object count so each new insert gets a new offset.
  const itemCount = Math.max(0, drawableObjects.length);
  const offsetMultiplier = itemCount % cascadeCount;

  const maxX = Math.max(24, referenceWidth - 100);
  const maxY = Math.max(24, referenceHeight - 100);

  return {
    left: clamp(defaultLeft + step * offsetMultiplier, 24, maxX),
    top: clamp(defaultTop + step * offsetMultiplier, 24, maxY),
  };
};

export { getNextInsertPosition };
